// Soft-delete with 30-day grace.
//
// Deletion request state lives in the private `profile_deletion_requests`
// table (owner/admin-readable only), not on `profiles`, so the public
// profile read surface cannot leak account-status info.
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const GRACE_DAYS = 30;

export const getMyAccountDeletionStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("profile_deletion_requests")
      .select("deletion_requested_at, deletion_scheduled_for")
      .eq("user_id", userId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return {
      requested: !!data?.deletion_requested_at,
      requestedAt: data?.deletion_requested_at ?? null,
      scheduledFor: data?.deletion_scheduled_for ?? null,
    };
  });

export const requestAccountDeletion = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { userId } = context;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: openTxns } = await supabaseAdmin
      .from("transactions")
      .select("id, status")
      .or(`buyer_id.eq.${userId},seller_id.eq.${userId}`)
      .in("status", ["pending_payment", "funds_held", "shipped", "delivered"]);
    if (openTxns && openTxns.length > 0) {
      throw new Error(
        `You have ${openTxns.length} active transaction(s). Complete or refund them before requesting deletion.`,
      );
    }

    const { data: openDisputes } = await supabaseAdmin
      .from("disputes")
      .select("id")
      .eq("opener_id", userId)
      .in("status", ["open", "investigating"]);
    if (openDisputes && openDisputes.length > 0) {
      throw new Error(
        `You have ${openDisputes.length} open dispute(s). They must be resolved before deletion.`,
      );
    }

    const now = new Date();
    const scheduled = new Date(now.getTime() + GRACE_DAYS * 24 * 60 * 60 * 1000);

    const { error: upsertErr } = await supabaseAdmin
      .from("profile_deletion_requests")
      .upsert(
        {
          user_id: userId,
          deletion_requested_at: now.toISOString(),
          deletion_scheduled_for: scheduled.toISOString(),
        },
        { onConflict: "user_id" },
      );
    if (upsertErr) throw new Error(upsertErr.message);

    await supabaseAdmin
      .from("buy_orders")
      .update({ status: "cancelled" })
      .eq("buyer_id", userId)
      .eq("status", "open");

    await supabaseAdmin
      .from("offers")
      .update({ status: "withdrawn" })
      .eq("seller_id", userId)
      .eq("status", "pending");

    return {
      scheduledFor: scheduled.toISOString(),
      requestedAt: now.toISOString(),
    };
  });

export const cancelAccountDeletion = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { userId } = context;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: req } = await supabaseAdmin
      .from("profile_deletion_requests")
      .select("deletion_requested_at, deletion_scheduled_for")
      .eq("user_id", userId)
      .maybeSingle();
    if (!req?.deletion_requested_at) {
      throw new Error("No deletion is currently scheduled");
    }
    if (
      req.deletion_scheduled_for &&
      new Date(req.deletion_scheduled_for).getTime() <= Date.now()
    ) {
      throw new Error("The grace period has already elapsed");
    }

    const { error } = await supabaseAdmin
      .from("profile_deletion_requests")
      .delete()
      .eq("user_id", userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
